import { useEffect, useRef } from 'react';
import { useRouter, useRouterState } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import fetchClient from '@eureca/candidate-api-sdk/client';
import { getMyIdentityConfirmationQueryKey } from '@eureca/candidate-api-sdk/hooks';
import type { GetMyIdentityConfirmationQueryResponse } from '@eureca/candidate-api-sdk/types';
import { getKeycloak } from '@/auth/keycloak-instance';
import { useMediaQuery } from '@/lib/use-media-query';
import {
  DESKTOP_MEDIA_QUERY,
  IdentityConfirmation,
  notifyIdentityConfirmed,
} from './IdentityConfirmation';
import {
  closeIdentityConfirmation,
  markIdentityConfirmed,
  openIdentityConfirmation,
  useIdentityConfirmationStore,
} from './identity-confirmation-store';
import {
  readAndClearPending,
  resolveProviderReturn,
  stripReturnParam,
} from './identity-reauth-return';

/**
 * DEV-555 — ponto único do modal "Confirme sua identidade" (root do router) e
 * tratamento do RETORNO do provedor social (AC-30/AC-31/AC-32).
 *
 * Ao montar, lê (e apaga) a pendência deixada na ida ao provedor e decide pelo
 * token atual + URL: sucesso → `POST { method: 'reauth' }`, toast e a ação que
 * abriu a confirmação executa na página de origem (`consumeCompletedAction`);
 * conta diferente → confirmação reaberta na origem com o alerta; interrompido
 * → confirmação reaberta sem alerta. O marcador `?identity-confirmation=…` é
 * removido da URL sem navegar.
 *
 * A confirmação pertence à página de ORIGEM: qualquer navegação para outra
 * página ("voltar" do navegador, menu) a fecha sem executar a ação (AC-34).
 */
export function IdentityConfirmationHost() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY);
  const handled = useRef(false);

  // "Voltar" do navegador (ou qualquer navegação) com a confirmação aberta: fecha
  // junto, nada executa. Compara só o pathname — `/perfil?confirm=x` → `/perfil`
  // é a mesma página de origem.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { open, request } = useIdentityConfirmationStore();
  useEffect(() => {
    if (!open || !request) return;
    if (pathname !== request.originPath.split('?')[0]) closeIdentityConfirmation();
  }, [pathname, open, request]);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;
    const pending = readAndClearPending();
    const search = window.location.search;
    const cleaned = stripReturnParam(window.location.href);
    if (cleaned !== window.location.href)
      window.history.replaceState(window.history.state, '', cleaned);
    if (!pending) return;

    const kc = getKeycloak();
    if (!kc.authenticated) return; // sessão encerrada no caminho: o fluxo normal de login assume (ACC-042)
    const result = resolveProviderReturn({
      pending,
      token: kc.tokenParsed as { sub?: string; auth_time?: number } | undefined,
      search,
    });
    if (result.kind === 'none') return;

    const goToOrigin = () => {
      const current = window.location.pathname + window.location.search;
      if (current !== pending.originPath) router.history.replace(pending.originPath);
    };

    if (result.kind === 'different-account') {
      openIdentityConfirmation(
        { action: pending.action, originPath: pending.originPath },
        { providerError: 'different-account' }
      );
      goToOrigin();
      return;
    }
    if (result.kind === 'interrupted') {
      openIdentityConfirmation({ action: pending.action, originPath: pending.originPath });
      goToOrigin();
      return;
    }

    void (async () => {
      try {
        const res = await fetchClient<GetMyIdentityConfirmationQueryResponse>({
          method: 'POST',
          url: '/me/identity-confirmation',
          data: { method: 'reauth' },
        });
        queryClient.setQueryData(getMyIdentityConfirmationQueryKey(), res.data);
        openIdentityConfirmation({ action: pending.action, originPath: pending.originPath });
        markIdentityConfirmed();
        notifyIdentityConfirmed(isDesktop);
      } catch {
        // 409 REAUTH_NOT_FRESH ou rede: trata como interrompido — a confirmação reabre, nada executa.
        openIdentityConfirmation({ action: pending.action, originPath: pending.originPath });
      }
      goToOrigin();
    })();
  }, [router, queryClient, isDesktop]);

  return <IdentityConfirmation />;
}
